'use client'

import classnames from 'classnames'
import styles from './AnimatedTextLines.module.scss'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import useWindowResize from '@/hooks/use-window-resize'
import useInView from '@/hooks/use-in-view'
import useStore from '@/store'
import gsap from 'gsap'
import { SplitText } from 'gsap/dist/SplitText'

gsap.registerPlugin(SplitText)

export const LINE_CLASS = styles.line
export const LINE_INNER_CLASS = styles.line__inner
export const WORD_CLASS = styles.word
export const CHAR_CLASS = styles.char

const AnimatedTextLines = forwardRef(
  (
    {
      className,
      children,
      querySelector,
      disableLineBreakCheck,
      autoAnimate = false,
      toAnimate = 'lines',
      animateInConfig,
      animateOutConfig,
      onInView,
      id,
    },
    ref,
  ) => {
    const $inner = useRef(null)
    const splitTextRef = useRef(null)
    const resizeKey = useWindowResize()
    const [rendered, setRendered] = useState(false)
    const debounceTimeout = useRef()
    const hasAnimatedRef = useRef(false)
    const [hasFinishesAnimated, setHasFinishesAnimated] = useState(false)
    const { setElementToObserve, isInView } = useInView()
    const pageIsTransitioning = useStore(state => state.pageIsTransitioning)
    const fontsLoaded = useStore(state => state.fontsLoaded)
    const hasAnimatedIn = useRef(false)
    const [enabledMasterRender, setEnabledMasterRender] = useState(true)
    const remValueCalculated = useStore(state => state.remValueCalculated)

    const getLines = () => {
      const linesNodeList = $inner?.current?.querySelectorAll(`.${LINE_CLASS}`)
      if (!linesNodeList) return
      const $lines = Array.from(linesNodeList)
      return $lines
    }

    const getLineInners = () => {
      const lineInnersNodeList = $inner?.current?.querySelectorAll(`.${LINE_INNER_CLASS}`)
      if (!lineInnersNodeList) return
      const $lineInners = Array.from(lineInnersNodeList)
      return $lineInners
    }

    const getWords = () => {
      const wordsNodeList = $inner?.current?.querySelectorAll(`.${WORD_CLASS}`)
      if (!wordsNodeList) return
      const $words = Array.from(wordsNodeList)
      return $words
    }

    const getChars = () => {
      const charsNodeList = $inner?.current?.querySelectorAll(`.${CHAR_CLASS}`)
      if (!charsNodeList) return
      const $chars = Array.from(charsNodeList)
      return $chars
    }

    const getElementsToAnimate = useCallback(() => {
      let $elements = getLineInners()
      if (toAnimate === 'words') {
        $elements = getWords()
      } else if (toAnimate === 'chars') {
        $elements = getChars()
      }

      return $elements
    }, [toAnimate])

    const animate = useCallback(
      (overrideConfig = {}) => {
        if (!splitTextRef.current || !$inner.current) return
        const $elements = getElementsToAnimate()
        if (!$elements?.length) return
        gsap.killTweensOf($elements)

        hasAnimatedIn.current = true

        let config = {
          y: 0,
          duration: 0.8,
          ease: 'Power4.easeOut',
          stagger: 0.1,
        }

        if (animateInConfig) {
          config = {
            ...config,
            ...animateInConfig,
          }
        }

        config = {
          ...config,
          ...overrideConfig,
        }

        gsap.to($elements, {
          ...config,
          onComplete: () => {
            setHasFinishesAnimated(true)
          },
        })
      },
      [animateInConfig, getElementsToAnimate],
    )

    const reset = useCallback(() => {
      // if (!splitTextRef.current || !$inner.current) return
      // const $elements = getElementsToAnimate()
      // if (!$elements.length) return
      // gsap.killTweensOf($elements)
      hasAnimatedIn.current = false
      // if (splitTextRef.current) splitTextRef.current.revert()
      // gsap.set($elements, { y: '110%' })
      setRendered(false)
      setHasFinishesAnimated(false)
      setEnabledMasterRender(false)
      setTimeout(() => {
        setEnabledMasterRender(true)
      }, 50)
    }, [])

    const animateOut = useCallback(
      (config = {}) => {
        if (!splitTextRef.current || !$inner.current) return
        const $elements = getElementsToAnimate()
        if (!$elements?.length) return
        gsap.killTweensOf($elements)

        let gsapConfig = {
          y: '-115%',
          duration: 0.4,
          stagger: 0.1,
        }

        if (animateOutConfig) {
          gsapConfig = {
            ...gsapConfig,
            ...animateOutConfig,
          }
        }

        if (config) {
          gsapConfig = {
            ...gsapConfig,
            ...config,
          }
        }

        gsap.to($elements, {
          ...gsapConfig,
          onComplete: () => {
            setHasFinishesAnimated(true)
          },
        })
      },
      [getElementsToAnimate, animateOutConfig],
    )

    useEffect(() => {
      if (!remValueCalculated) return
      if (!enabledMasterRender) return
      if (!fontsLoaded) return
      if (!$inner.current) return
      const elementsNodeList = $inner.current?.querySelectorAll(querySelector)
      const elements = Array.from(elementsNodeList)
      if (!elements?.length) return
      if (hasAnimatedIn.current) return

      if (!disableLineBreakCheck) {
        elements.forEach(el => {
          if (!el) return
          if (el.textContent?.includes('\n')) {
            const text = el.textContent.replace(/(?:\r\n|\r|\n)/g, '<br/>')
            el.innerHTML = text
          }
        })
      }

      if (splitTextRef.current) splitTextRef.current.revert()

      let type = 'lines'
      if (toAnimate === 'words') {
        type = 'lines,words'
      } else if (toAnimate === 'chars') {
        type = 'lines,chars'
      }

      splitTextRef.current = new SplitText(elements, {
        type,
        linesClass: LINE_CLASS,
        wordsClass: WORD_CLASS,
        charsClass: CHAR_CLASS,
      })

      const $lines = getLines()
      $lines.forEach(el => {
        const html = el.innerHTML
        const newHtml = `<span class="${LINE_INNER_CLASS}">${html}</span>`
        el.innerHTML = newHtml
      })

      const $lineElements = getLines()
      const $lineInnerElements = getLineInners()
      const $wordElements = getWords()
      const $charElements = getChars()

      $lineElements.forEach($el => {
        if (!$el) return
        $el.dataset.line = 'true'
      })

      $lineInnerElements.forEach($el => {
        if (!$el) return
        $el.dataset.lineInner = 'true'
      })

      $wordElements.forEach($el => {
        if (!$el) return
        $el.dataset.word = 'true'
      })

      $charElements.forEach($el => {
        if (!$el) return
        $el.dataset.char = 'true'
      })

      setTimeout(() => {
        setRendered(true)
      }, 10)
    }, [
      querySelector,
      resizeKey,
      disableLineBreakCheck,
      fontsLoaded,
      toAnimate,
      enabledMasterRender,
      remValueCalculated,
    ])

    // useEffect(() => {
    //   return
    //   reset()
    // }, [children])

    useEffect(() => {
      if (!rendered) return
      if (!autoAnimate) return
      if (!fontsLoaded) return
      if (pageIsTransitioning) return
      if (!isInView || hasAnimatedIn.current) return
      hasAnimatedIn.current = true

      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }

      debounceTimeout.current = setTimeout(() => {
        if (hasAnimatedRef.current) return
        hasAnimatedRef.current = true
        animate()
        if (onInView) onInView()
      }, 75)
    }, [rendered, isInView, pageIsTransitioning, fontsLoaded, autoAnimate, animate, onInView, id])

    useImperativeHandle(ref, () => ({
      animate,
      animateOut,
      getWords,
      getLines,
      getChars,
      getLineInners,
      reset,
      updateText: text => {
        const $elements = getElementsToAnimate()
        if (!$elements?.length) return
        if ($elements.length === 1) {
          $elements[0].textContent = text
        } else {
          console.warn(`Multiple ${toAnimate} elements found, updateText only works with one element`)
        }
      },
    }))

    if (!enabledMasterRender) return null

    return (
      <div
        className={classnames(
          styles.AnimatedTextLines,
          className,
          { [styles.hasFinishesAnimated]: hasFinishesAnimated },
          { [styles.rendered]: rendered },
        )}
        data-to-animate={toAnimate}
        ref={ref => {
          setElementToObserve(ref)
        }}
      >
        <div
          ref={ref => {
            $inner.current = ref
          }}
          className={styles.inner}
        >
          {children}
        </div>
      </div>
    )
  },
)

AnimatedTextLines.displayName = 'AnimatedTextLines'

export default AnimatedTextLines
